import React from 'react';
import { AlertTriangle, ArrowLeft, Shield, XCircle } from 'lucide-react';
interface DisclaimersProps {
  onNavigate: (page: string) => void;
}
export function Disclaimers({
  onNavigate
}: DisclaimersProps) {
  return <div className="min-h-screen bg-white" data-id="element-385">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-386">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-387">
          <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-388">
            <ArrowLeft className="w-4 h-4" data-id="element-389" />
            <span className="text-sm font-medium" data-id="element-390">Back to Home</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-391">
        <div className="mb-8" data-id="element-392">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-393">
            Legal Disclaimers
          </h1>
          <p className="text-gray-600" data-id="element-394">Last Updated: January 27, 2026</p>
        </div>

        {/* Critical Warnings Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-395">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6" data-id="element-396">
            <div className="flex items-start gap-3 mb-3" data-id="element-397">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-398" />
              <h3 className="font-bold text-red-900 text-lg" data-id="element-399">
                NOT Legal Advice
              </h3>
            </div>
            <p className="text-red-800 text-sm" data-id="element-400">
              We are NOT attorneys. This is NOT legal advice. Consult a licensed
              attorney for legal guidance.
            </p>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6" data-id="element-401">
            <div className="flex items-start gap-3 mb-3" data-id="element-402">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" data-id="element-403" />
              <h3 className="font-bold text-yellow-900 text-lg" data-id="element-404">
                NOT HIPAA Compliant
              </h3>
            </div>
            <p className="text-yellow-800 text-sm" data-id="element-405">
              Data is NOT encrypted or HIPAA compliant. Do not enter highly
              sensitive medical information.
            </p>
          </div>

          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6" data-id="element-406">
            <div className="flex items-start gap-3 mb-3" data-id="element-407">
              <Shield className="w-6 h-6 text-orange-600 flex-shrink-0" data-id="element-408" />
              <h3 className="font-bold text-orange-900 text-lg" data-id="element-409">
                Beta Version Only
              </h3>
            </div>
            <p className="text-orange-800 text-sm" data-id="element-410">
              This is a testing prototype. No warranties. Data may be lost.
              Export backups regularly.
            </p>
          </div>

          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6" data-id="element-411">
            <div className="flex items-start gap-3 mb-3" data-id="element-412">
              <AlertTriangle className="w-6 h-6 text-purple-600 flex-shrink-0" data-id="element-413" />
              <h3 className="font-bold text-purple-900 text-lg" data-id="element-414">
                No Guarantees
              </h3>
            </div>
            <p className="text-purple-800 text-sm" data-id="element-415">
              No guarantee of admissibility in court. No guarantee of legal
              outcomes. Use at your own risk.
            </p>
          </div>
        </div>

        <div className="prose prose-gray max-w-none" data-id="element-416">
          {/* Disclaimer 1 */}
          <section className="mb-10" data-id="element-417">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-418">
              1. NOT LEGAL ADVICE - NO ATTORNEY-CLIENT RELATIONSHIP
            </h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-419">
              <p className="text-red-900 font-bold mb-3 text-lg" data-id="element-420">
                CRITICAL: READ THIS CAREFULLY
              </p>
              <div className="space-y-3 text-red-800" data-id="element-421">
                <p className="font-bold" data-id="element-422">
                  Zapiis is a documentation tool. We are NOT a law firm.
                </p>

                <p data-id="element-423">
                  <strong data-id="element-424">We are NOT attorneys</strong> and do NOT provide:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-425">
                  <li data-id="element-426">Legal advice of any kind</li>
                  <li data-id="element-427">Legal representation</li>
                  <li data-id="element-428">Legal services</li>
                  <li data-id="element-429">Legal opinions or recommendations</li>
                  <li data-id="element-430">Guidance on what legal actions to take or not take</li>
                </ul>

                <p className="mt-4" data-id="element-431">
                  <strong data-id="element-432">Using this Service does NOT create:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-433">
                  <li data-id="element-434">An attorney-client relationship</li>
                  <li data-id="element-435">Any confidential or privileged relationship</li>
                  <li data-id="element-436">Any duty of care or fiduciary duty</li>
                </ul>

                <p className="mt-4 font-bold" data-id="element-437">
                  NOTHING in this Service should be construed as legal advice or
                  used as a substitute for consultation with a licensed
                  attorney.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-438">
              What This Means for You:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-439">
              <li data-id="element-440">
                You MUST consult with a licensed attorney in your jurisdiction
                for legal advice specific to your situation
              </li>
              <li data-id="element-441">
                Do NOT rely on any information, suggestions, or AI-generated
                content from this Service as legal advice
              </li>
              <li data-id="element-442">
                Do NOT make legal decisions based solely on documentation
                created with this Service
              </li>
              <li data-id="element-443">
                We do NOT tell you what to do - we only provide a tool to
                organize information
              </li>
              <li data-id="element-444">
                We are NOT responsible for any legal outcomes or consequences of
                your actions
              </li>
            </ul>
          </section>

          {/* Disclaimer 2 */}
          <section className="mb-10" data-id="element-445">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-446">
              2. NOT HIPAA COMPLIANT - DATA SECURITY LIMITATIONS
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-4" data-id="element-447">
              <p className="text-yellow-900 font-bold mb-3 text-lg" data-id="element-448">
                BETA VERSION - LIMITED SECURITY
              </p>
              <div className="space-y-3 text-yellow-800" data-id="element-449">
                <p className="font-bold" data-id="element-450">
                  This beta version is NOT compliant with HIPAA (Health
                  Insurance Portability and Accountability Act).
                </p>

                <p data-id="element-451">
                  <strong data-id="element-452">Data Storage Limitations:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2" data-id="element-453">
                  <li data-id="element-454">All data is stored in your browser's localStorage</li>
                  <li data-id="element-455">Data is NOT encrypted at rest</li>
                  <li data-id="element-456">Data is NOT encrypted to HIPAA standards</li>
                  <li data-id="element-457">Data is NOT backed up to secure cloud servers</li>
                  <li data-id="element-458">Data is NOT protected by enterprise-grade security</li>
                  <li data-id="element-459">
                    Clearing your browser cache will permanently delete all data
                  </li>
                </ul>

                <p className="mt-4 font-bold text-red-700" data-id="element-460">
                  DO NOT ENTER HIGHLY SENSITIVE INFORMATION including:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-red-700" data-id="element-461">
                  <li data-id="element-462">
                    Protected Health Information (PHI) requiring HIPAA
                    protection
                  </li>
                  <li data-id="element-463">Detailed medical records or diagnoses</li>
                  <li data-id="element-464">Social Security Numbers</li>
                  <li data-id="element-465">Financial account information</li>
                  <li data-id="element-466">Passwords or security credentials</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-467">
              Your Responsibilities:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-468">
              <li data-id="element-469">
                Regularly export backups of your data using the export
                functionality
              </li>
              <li data-id="element-470">
                Understand that data loss is possible and we cannot recover lost
                data
              </li>
              <li data-id="element-471">Protect your device and browser from unauthorized access</li>
              <li data-id="element-472">
                Do not use this Service for information requiring HIPAA-level
                protection
              </li>
              <li data-id="element-473">
                Wait for the full HIPAA-compliant version before entering
                sensitive medical information
              </li>
            </ul>
          </section>

          {/* Disclaimer 3 */}
          <section className="mb-10" data-id="element-474">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-475">
              3. NO WARRANTY - PROVIDED "AS IS"
            </h2>
            <div className="bg-gray-100 border-l-4 border-gray-500 p-6 mb-4" data-id="element-476">
              <p className="text-gray-900 font-bold mb-2" data-id="element-477">
                The Service is provided "AS IS" and "AS AVAILABLE" without
                warranties of any kind.
              </p>
              <p className="text-gray-700 text-sm" data-id="element-478">
                We make NO warranties, express or implied, including but not
                limited to warranties of merchantability, fitness for a
                particular purpose, title, or non-infringement.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-479">
              We Do NOT Warrant That:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-480">
              <li data-id="element-481">The Service will be uninterrupted, secure, or error-free</li>
              <li data-id="element-482">
                The Service will meet your specific requirements or expectations
              </li>
              <li data-id="element-483">
                Documentation created will be admissible as evidence in court
              </li>
              <li data-id="element-484">
                AI-generated content will be accurate, complete, or reliable
              </li>
              <li data-id="element-485">Your data will be preserved or recoverable if lost</li>
              <li data-id="element-486">The Service will result in any particular legal outcome</li>
            </ul>
          </section>

          {/* Disclaimer 4 */}
          <section className="mb-10" data-id="element-487">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-488">
              4. NO GUARANTEE OF ADMISSIBILITY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-489">
              <strong data-id="element-490">
                We make NO representations or warranties that documentation
                created using this Service will be admissible as evidence in any
                legal proceeding.
              </strong>
            </p>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-491">
              Admissibility of evidence is determined by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-492">
              <li data-id="element-493">Courts based on applicable rules of evidence</li>
              <li data-id="element-494">Jurisdiction-specific laws and procedures</li>
              <li data-id="element-495">The discretion of judges and legal standards</li>
              <li data-id="element-496">Proper authentication and chain of custody requirements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-497">
              <strong data-id="element-498">You MUST consult with an attorney</strong> regarding
              evidence requirements, admissibility standards, and proper
              documentation procedures for your specific case and jurisdiction.
            </p>
          </section>

          {/* Disclaimer 5 */}
          <section className="mb-10" data-id="element-499">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-500">
              5. AI-GENERATED CONTENT DISCLAIMER
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-501">
              <p className="text-blue-900 font-bold mb-2" data-id="element-502">
                Artificial Intelligence Limitations
              </p>
              <p className="text-blue-800 text-sm" data-id="element-503">
                Features using AI (case summaries, attorney search) may produce
                inaccurate, incomplete, or misleading information. Always verify
                AI-generated content independently.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-504">
              AI Content Warnings:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-505">
              <li data-id="element-506">
                AI-generated case summaries may contain errors or omissions
              </li>
              <li data-id="element-507">
                Attorney recommendations are NOT endorsements and should be
                independently verified
              </li>
              <li data-id="element-508">
                AI cannot provide legal advice or professional recommendations
              </li>
              <li data-id="element-509">
                AI may misinterpret your information or provide irrelevant
                suggestions
              </li>
              <li data-id="element-510">
                You are responsible for reviewing and verifying all AI-generated
                content
              </li>
              <li data-id="element-511">
                Do NOT rely solely on AI-generated content for legal decisions
              </li>
            </ul>
          </section>

          {/* Disclaimer 6 */}
          <section className="mb-10" data-id="element-512">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-513">
              6. LIMITATION OF LIABILITY
            </h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-4" data-id="element-514">
              <p className="text-red-900 font-bold mb-2" data-id="element-515">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <p className="text-red-800 text-sm" data-id="element-516">
                Zapiis and its affiliates, employees, contractors, and partners
                SHALL NOT BE LIABLE for any damages arising from your use or
                inability to use the Service.
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-517">
              We Are NOT Liable For:
            </h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-518">
              <li data-id="element-519">Loss of data, revenue, profits, or business opportunities</li>
              <li data-id="element-520">
                Legal outcomes or consequences of actions taken based on
                documentation created with the Service
              </li>
              <li data-id="element-521">
                Damages resulting from reliance on any information provided by
                the Service
              </li>
              <li data-id="element-522">
                Indirect, incidental, special, consequential, or punitive
                damages
              </li>
              <li data-id="element-523">
                Damages from data breaches, unauthorized access, or security
                incidents
              </li>
              <li data-id="element-524">Damages from errors, bugs, or technical failures</li>
              <li data-id="element-525">Damages from AI-generated content or recommendations</li>
            </ul>
          </section>

          {/* Disclaimer 7 */}
          <section className="mb-10" data-id="element-526">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-527">
              7. NO PROFESSIONAL RECOMMENDATIONS
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-528">
              <strong data-id="element-529">We do NOT provide recommendations on:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-530">
              <li data-id="element-531">What legal actions you should or should not take</li>
              <li data-id="element-532">Whether you have a valid legal case</li>
              <li data-id="element-533">Which attorney you should hire</li>
              <li data-id="element-534">How to present your case or evidence</li>
              <li data-id="element-535">What information to include or exclude from documentation</li>
              <li data-id="element-536">Legal strategy or tactics</li>
              <li data-id="element-537">Settlement decisions or negotiations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-538">
              All decisions regarding your legal matters are yours alone to
              make, preferably in consultation with a licensed attorney.
            </p>
          </section>

          {/* Disclaimer 8 */}
          <section className="mb-10" data-id="element-539">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-540">
              8. BETA VERSION - TESTING PURPOSES ONLY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-541">
              This is a <strong data-id="element-542">beta testing version</strong> of the Service.
              Beta versions are:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-543">
              <li data-id="element-544">Incomplete and subject to significant changes</li>
              <li data-id="element-545">Not fully tested and may contain bugs or errors</li>
              <li data-id="element-546">Provided for testing and feedback purposes only</li>
              <li data-id="element-547">
                Not intended for production use with real, sensitive cases
              </li>
              <li data-id="element-548">
                Subject to termination or modification at any time without
                notice
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-549">
              <strong data-id="element-550">Use this beta version at your own risk.</strong> Wait for
              the full release with HIPAA compliance and enterprise security
              before documenting highly sensitive cases.
            </p>
          </section>

          {/* Disclaimer 9 */}
          <section className="mb-10" data-id="element-551">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-552">
              9. USER RESPONSIBILITY
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-553">
              <strong data-id="element-554">You are solely responsible for:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-555">
              <li data-id="element-556">
                The accuracy and completeness of all information you enter
              </li>
              <li data-id="element-557">
                Determining whether this Service is appropriate for your needs
              </li>
              <li data-id="element-558">
                Consulting with qualified legal counsel before taking legal
                action
              </li>
              <li data-id="element-559">Verifying all AI-generated content and recommendations</li>
              <li data-id="element-560">Maintaining backups of your data</li>
              <li data-id="element-561">Protecting your device and preventing unauthorized access</li>
              <li data-id="element-562">
                Complying with all applicable laws in your use of the Service
              </li>
              <li data-id="element-563">Any consequences resulting from your use of the Service</li>
            </ul>
          </section>

          {/* Disclaimer 10 */}
          <section className="mb-10" data-id="element-564">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-565">
              10. CHANGES AND UPDATES
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-566">
              We reserve the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-567">
              <li data-id="element-568">
                Modify, suspend, or discontinue the Service at any time without
                notice
              </li>
              <li data-id="element-569">Update these disclaimers at any time</li>
              <li data-id="element-570">Change features, functionality, or pricing without notice</li>
              <li data-id="element-571">Terminate beta access for any reason</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-572">
              Continued use of the Service after changes constitutes acceptance
              of the modified disclaimers.
            </p>
          </section>

          {/* Final Acknowledgment */}
          <div className="bg-gray-900 text-white rounded-lg p-8 mt-12" data-id="element-573">
            <h3 className="text-xl font-bold mb-4" data-id="element-574">
              BY USING THIS SERVICE, YOU ACKNOWLEDGE AND AGREE THAT:
            </h3>
            <ul className="space-y-3 text-sm" data-id="element-575">
              <li className="flex items-start gap-2" data-id="element-576">
                <span className="text-yellow-400 font-bold" data-id="element-577">✓</span>
                <span data-id="element-578">
                  You have read and understood all disclaimers on this page
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-579">
                <span className="text-yellow-400 font-bold" data-id="element-580">✓</span>
                <span data-id="element-581">
                  You understand this is NOT legal advice and we are NOT
                  attorneys
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-582">
                <span className="text-yellow-400 font-bold" data-id="element-583">✓</span>
                <span data-id="element-584">
                  You understand data is NOT HIPAA compliant or encrypted
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-585">
                <span className="text-yellow-400 font-bold" data-id="element-586">✓</span>
                <span data-id="element-587">
                  You will consult with a licensed attorney for legal guidance
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-588">
                <span className="text-yellow-400 font-bold" data-id="element-589">✓</span>
                <span data-id="element-590">
                  You accept all risks associated with using this beta Service
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-591">
                <span className="text-yellow-400 font-bold" data-id="element-592">✓</span>
                <span data-id="element-593">
                  You will not hold Zapiis liable for any damages or legal
                  outcomes
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-594">
                <span className="text-yellow-400 font-bold" data-id="element-595">✓</span>
                <span data-id="element-596">You will regularly export backups of your data</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="mt-12 text-center" data-id="element-597">
            <p className="text-gray-600 mb-2" data-id="element-598">
              Questions about these disclaimers?
            </p>
            <p className="text-gray-900 font-medium" data-id="element-599">
              Contact: [Your Legal Contact Email]
            </p>
          </div>
        </div>
      </main>
    </div>;
}